import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, getToken, API } from "@/lib/api";
import { Trash2, Download } from "lucide-react";

const SOURCE_LABELS = {
  "pattern-reveal": "Pattern Reveal",
  "four-levels": "Four Levels",
  "weekly-edge": "Weekly Edge",
  "daily-edge": "Daily Edge",
};

const sourceBadge = (source) => {
  const map = {
    "pattern-reveal": "bg-brand-blue/10 text-brand-blue",
    "four-levels": "bg-brand-purple/10 text-brand-purple",
    "weekly-edge": "bg-brand-turquoise/15 text-brand-turquoise",
    "daily-edge": "bg-brand-green/15 text-brand-green",
  };
  return map[source] || "bg-brand-rule/40 text-brand-ink/60";
};

export default function AdminWaitlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/waitlist");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (w) => {
    if (!window.confirm(`Remove ${w.email} from the waitlist?`)) return;
    await api.delete(`/admin/waitlist/${w.id}`);
    toast.success("Removed.");
    load();
  };

  const downloadCsv = async () => {
    const token = getToken();
    const res = await fetch(`${API}/admin/waitlist.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "waitlist.csv";
    a.click();
  };

  const sources = useMemo(() => {
    const s = Array.from(new Set(items.map((i) => i.source || "pattern-reveal")));
    return s.sort();
  }, [items]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((i) => {
      const s = i.source || "pattern-reveal";
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [items]);

  const filtered = filter === "all" ? items : items.filter((i) => (i.source || "pattern-reveal") === filter);

  return (
    <div data-testid="admin-waitlist">
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="eyebrow mb-2">Signups</div>
          <h1 className="display text-[36px]">Interest &amp; Waitlist</h1>
        </div>
        <button
          onClick={downloadCsv}
          data-testid="export-waitlist"
          className="inline-flex items-center gap-2 rounded-full border border-brand-ink/25 px-5 py-2.5 text-[13.5px] text-brand-ink hover:border-brand-blue hover:text-brand-blue transition-colors"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Source filter chips */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="waitlist-source-filter">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">
          All <span className="opacity-60 ml-1">{counts.all || 0}</span>
        </FilterChip>
        {sources.map((s) => (
          <FilterChip
            key={s}
            active={filter === s}
            onClick={() => setFilter(s)}
            testId={`filter-${s}`}
          >
            {SOURCE_LABELS[s] || s} <span className="opacity-60 ml-1">{counts[s] || 0}</span>
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <p className="text-brand-ink/50 text-[14px]">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-brand-rule bg-brand-cream/40 p-10 text-center">
          <p className="text-brand-ink/70">Nothing here yet.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-brand-rule/70 overflow-hidden bg-brand-cream">
          <table className="w-full text-[14px]">
            <thead className="text-left text-brand-ink/60 border-b border-brand-rule/70">
              <tr>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium">Note</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} className="border-b border-brand-rule/40 last:border-0 align-top" data-testid={`waitlist-row-${w.id}`}>
                  <td className="px-5 py-4 font-medium text-brand-ink">
                    <a href={`mailto:${w.email}`} className="hover:text-brand-blue">{w.email}</a>
                  </td>
                  <td className="px-5 py-4 text-brand-ink/70">{w.name || "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] ${sourceBadge(w.source)}`}>
                      {SOURCE_LABELS[w.source] || w.source}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-brand-ink/75 max-w-md">
                    {w.note ? (
                      <span className="whitespace-pre-wrap text-[13.5px] leading-relaxed">{w.note}</span>
                    ) : (
                      <span className="text-brand-ink/30">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-brand-ink/50 text-[13px] whitespace-nowrap">
                    {new Date(w.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => remove(w)}
                      data-testid={`delete-waitlist-${w.id}`}
                      className="inline-flex items-center gap-1.5 text-brand-ink/70 hover:text-red-600 text-[13px]"
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-[12.5px] border transition-colors ${
        active
          ? "bg-brand-ink text-brand-cream border-brand-ink"
          : "text-brand-ink/70 border-brand-rule hover:border-brand-blue hover:text-brand-blue"
      }`}
    >
      {children}
    </button>
  );
}
